import { HttpClient } from "@angular/common/http";
import { Injectable, OnDestroy } from "@angular/core";
import { FileCollection, UploadService } from "@merri/common";
import { BehaviorSubject, catchError, filter, map, Observable, of, Subject, switchMap, take, takeUntil, throwError } from "rxjs";
import { AppConfigService, LocalStorageKeys, PartnerTypes, PartnerUserService, SeoService, StorageService } from "src/app/core";
import { HelperMethods } from "src/app/shared/helper-methods";
import { RoomBasic, Venue, VenueBasic, VenuesResponses } from "../../venues";
import { Category } from "../../visionboard";
import { VisionboardCategoriesResponses } from "../../visionboard/responses";
import { VenueAdminCommands } from "../commands/venue-admin-commands";
import { ScreenshotLegendPlacement } from "../models/enums/screenshot-legend-placement";
import { VenueAdminRoom } from "../models/venue-admin-room";
import { VenueAdminVenueInfo } from "../models/venue-admin-venue-info";
import {
  GetVenueAdminVenueInfoResponse,
  GetVenueAdminVenueRoomResponse,
  GetVenueAdminVenuesResponse,
  GetVenueItemsCategoriesResponse,
  PostVenueAdminRoomResponse,
  PostVenueAdminVenueResponse,
  PostVenueTemplateDeactivateResponse,
  PostVenueTemplateNameResponse,
  VenueAdminResponses,
} from "../responses/venue-admin-responses";

@Injectable({
  providedIn: "root",
})
export class VenueAdminService implements OnDestroy {
  private _destroyed$: Subject<void> = new Subject<void>();

  private _allVenues$: BehaviorSubject<VenueBasic[]> = new BehaviorSubject<VenueBasic[]>(null);
  allVenues$: Observable<VenueBasic[]> = this._allVenues$.asObservable();

  private _activeVenue$: BehaviorSubject<VenueBasic> = new BehaviorSubject<VenueBasic>(null);
  activeVenue$: Observable<VenueBasic> = this._activeVenue$.asObservable();

  private _hideBanner$: Subject<boolean> = new Subject<boolean>();
  hideBanner$: Observable<boolean> = this._hideBanner$.asObservable();

  /**
   * confirmVersionChange is utilized by the venue info page and the venue picker.  When the venue info page
   * is dirty, it sets this boolean to true so that the venue picker knows to prompt when a user tries
   * to change the venue while the form is dirty.
   */
  private _confirmVersionChange: boolean;

  get confirmVersionChange(): boolean {
    return this._confirmVersionChange;
  }

  set confirmVersionChange(value: boolean) {
    this._confirmVersionChange = value;
  }

  constructor(
    private _seoService: SeoService,
    private _appConfigService: AppConfigService,
    private _http: HttpClient,
    private _uploadService: UploadService,
    private _storageService: StorageService,
    private _partnerUserService: PartnerUserService,
  ) {
    this._partnerUserService.currentPartnerUser$
      .pipe(
        filter(user => !!user),
        switchMap(() => {
          if (this._partnerUserService.hasPartnerType(PartnerTypes.Venue)) {
            return this.loadVenues$();
          }

          this._allVenues$.next(null);
          return of(null);
        }),
      )
      .pipe(takeUntil(this._destroyed$))
      .subscribe();
  }

  ngOnDestroy(): void {
    this._destroyed$.next();
    this._destroyed$.complete();
  }

  setVenueAdminTitle(postFix: string): void {
    this._seoService.postFixTitle(`Venue Settings - ${postFix}`);
  }

  get activeVenue(): VenueBasic {
    return this._activeVenue$.value;
  }

  setHideBanner(hide: boolean) {
    this._hideBanner$.next(hide);
  }

  setActiveVenueById(venueId: number): void {
    this._storageService.local.set(LocalStorageKeys.VenueAdminCurrentVenue, venueId.toString());
    this._activeVenue$.next(this.allVenues.find(v => v.id === venueId));
  }

  get allVenues(): VenueBasic[] {
    return this._allVenues$.value;
  }

  updateVenueRoom(room: RoomBasic, updatedRoom: VenueAdminRoom): void {
    room.roomName = updatedRoom.roomName;
    room.primaryImageUrl = updatedRoom.primaryImageUrl;
  }

  loadVenues$(): Observable<VenueBasic[]> {
    return this._http
      .get<GetVenueAdminVenuesResponse>(this._appConfigService.getProtectedConfig().venueAdminVenuesUrl)
      .pipe(takeUntil(this._destroyed$))
      .pipe(
        map(response => {
          const venueAdminVenues = VenueAdminResponses.getVenueAdminVenuesBasicFromResponse(response).sort((a, b) =>
            HelperMethods.sortString(a.name, b.name),
          );

          this._allVenues$.next(venueAdminVenues);
          return venueAdminVenues;
        }),
        catchError(error => {
          return throwError(() => error);
        }),
      );
  }

  getVenueInfo$(venueId: number): Observable<VenueAdminVenueInfo> {
    return this._http
      .get<GetVenueAdminVenueInfoResponse>(this._appConfigService.getVenueAdminVenueUrl(venueId))

      .pipe(map(response => VenueAdminResponses.getVenueAdminVenueInfoFromResponse(response)));
  }

  saveVenueInfo$(
    venueId: number,
    phone: string,
    email: string,
    website: string,
    market: string,
    setting: string,
    style: string,
    type: string,
    files: FileCollection,
    notificationsEmail: string,
  ): Observable<Venue> {
    const command = VenueAdminCommands.getPostVenueAdminVenueCommand(
      phone,
      email,
      website,
      market,
      setting,
      style,
      type,
      notificationsEmail,
    );

    return this._uploadService
      .uploadFiles$<PostVenueAdminVenueResponse>(files, this._appConfigService.getVenueAdminVenueUrl(venueId), JSON.stringify(command))
      .pipe(
        filter(resp => !!resp.response),
        take(1),
        map(resp => resp.response),
        map(response => {
          this._activeVenue$.next(VenuesResponses.getVenueBasicFromVenue(response.venue));
          return VenuesResponses.getVenue(response.venue);
        }),
      );
  }

  getVenueRoom$(venueId: number, roomId: number): Observable<VenueAdminRoom> {
    return this._http.get<GetVenueAdminVenueRoomResponse>(this._appConfigService.getVenueAdminRoomUrl(venueId, roomId)).pipe(
      map(response => {
        const room = VenueAdminResponses.getVenueAdminRoomFromResponse(response?.room);
        return room;
      }),
    );
  }

  saveVenueRoom$(
    venueId: number,
    roomId: number,
    roomName: string,
    maxCapacity: number,
    distanceBetweenChairsInches: number,
    allowsChandeliers: boolean,
    displayIconLegendInScreenshots: boolean,
    screenshotLegendPlacement: ScreenshotLegendPlacement,
    files: FileCollection,
  ): Observable<VenueAdminRoom> {
    const command = VenueAdminCommands.getPostVenueRoomCommand(
      roomName,
      maxCapacity,
      distanceBetweenChairsInches,
      allowsChandeliers,
      displayIconLegendInScreenshots,
      screenshotLegendPlacement,
    );

    return this._uploadService
      .uploadFiles$<PostVenueAdminRoomResponse>(
        files,
        this._appConfigService.getVenueAdminRoomUrl(venueId, roomId),
        JSON.stringify(command),
      )
      .pipe(
        filter(resp => !!resp.response),
        take(1),
        map(resp => resp.response),
        map(response => {
          return VenueAdminResponses.getVenueAdminRoomFromResponse(response.room);
        }),
      );
  }

  getVenueInventoryCategories$(venueId: number): Observable<Category[]> {
    return this._http.get<GetVenueItemsCategoriesResponse>(this._appConfigService.getVenueAdminInventoryCategoriesUrl(venueId)).pipe(
      map(response => {
        return response?.categories?.map(c => VisionboardCategoriesResponses.getVisionboardCategory(c)) ?? [];
      }),
    );
  }

  updateTemplateName$(templateId: string, templateName: string): Observable<boolean> {
    return this._http
      .post<PostVenueTemplateNameResponse>(
        this._appConfigService.getVenueAdminTemplatesPresetsTemplateNameUrl(this.activeVenue.id),
        VenueAdminCommands.getPostVenueTemplateNameCommand(templateId, templateName),
      )
      .pipe(map(response => response.success));
  }

  deactivateTemplate$(templateId: string): Observable<boolean> {
    return this._http
      .post<PostVenueTemplateDeactivateResponse>(
        this._appConfigService.getVenueAdminTemplatesPresetsTemplateDeactivateUrl(this.activeVenue.id),
        VenueAdminCommands.getPostVenueTemplateDeactivateCommmand(templateId),
      )
      .pipe(map(response => response.success));
  }
}
